import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertTriangle, TrendingUp, ArrowRight, Zap, Sun, Loader2, CheckCircle2 } from "lucide-react";

/**
 * Aujourd'hui — vue agrégée des projections 3Q de tous les modules Noam.
 *
 * V23.15.40 — Rappel doctrinal : les 3Q sont une LENTILLE DE LECTURE, pas
 * un remplacement des 51 doctrines. Cette page projette la sortie des doctrines
 * dans une file d'attente priorisée.
 */
export default function Aujourdhui() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/workspace/three-q/today");
        setData(r.data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const now = new Date();
  const dateStr = now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="space-y-5" data-testid="aujourdhui-root">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-[#EFD127] noam-overline">
            <Sun className="w-3 h-3" /> Vue synthétique · projection Doctrine 3Q
          </div>
          <h1 className="noam-display text-3xl font-bold tracking-tight mt-1">
            Aujourd&apos;hui
          </h1>
          <p className="text-zinc-400 text-sm mt-2 max-w-3xl leading-relaxed">
            La lentille 3Q — <span className="text-zinc-200">« Et alors ? · Pourquoi maintenant ? · Que fais-je ? »</span> — appliquée à tous tes modules.
            Les items ci-dessous sont ceux où Noam détecte une <strong>urgence doctrinale</strong> à traiter maintenant.
            Les 51 doctrines restent le moteur ; cette page en projette la sortie.
          </p>
          <p className="text-[11px] text-zinc-600 mt-1">{dateStr}</p>
        </div>
      </div>

      {loading && (
        <div className="noam-card p-8 text-center text-zinc-400 text-sm" data-testid="aujourdhui-loading">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          Consolidation des projections en cours…
        </div>
      )}

      {!loading && data && data.count === 0 && (
        <div className="noam-card p-10 text-center" data-testid="aujourdhui-empty">
          <div className="w-14 h-14 mx-auto rounded-xl bg-[#1D9E75]/10 border border-[#1D9E75]/30 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-[#1D9E75]" />
          </div>
          <h2 className="noam-display text-xl font-semibold mt-4">Aucune urgence doctrinale aujourd&apos;hui</h2>
          <p className="text-zinc-400 text-sm mt-2 max-w-md mx-auto">
            La lentille 3Q ne détecte aucun signal critique ou d&apos;alerte sur tes modules.
            C&apos;est le moment idéal pour approfondir une doctrine, préparer un pitch ou lancer une analyse Bras Armé.
          </p>
          <div className="mt-6 flex items-center justify-center gap-2 flex-wrap">
            <Link to="/app/chat" className="btn-secondary text-sm" data-testid="aujourdhui-chat">
              Discuter avec NOAM
            </Link>
            <Link to="/app/origination" className="btn-primary text-sm" data-testid="aujourdhui-origination">
              Lancer Bras Armé
            </Link>
          </div>
        </div>
      )}

      {!loading && data && data.count > 0 && (
        <>
          <div className="text-xs text-zinc-500 flex items-center gap-2" data-testid="aujourdhui-count">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/[0.04] border border-white/10">
              {data.count} item{data.count > 1 ? "s" : ""} prioritaire{data.count > 1 ? "s" : ""}
            </span>
            <span className="text-[10px] italic">
              — triés par sévérité doctrinale
            </span>
          </div>

          <div className="space-y-3" data-testid="aujourdhui-list">
            {data.items.map((it, i) => (
              <PriorityCard key={`${it.module_key}-${i}`} item={it} index={i} />
            ))}
          </div>

          <div className="text-[10px] text-zinc-600 italic mt-4 border-t border-white/5 pt-3" data-testid="aujourdhui-reminder">
            {data.doctrine_reminder}
          </div>
        </>
      )}
    </div>
  );
}

function PriorityCard({ item, index }) {
  const color = item.color || "#EF9F27";
  const Icon = item.severity === "critical" ? AlertTriangle : TrendingUp;

  return (
    <Link
      to={item.cta.href}
      className="noam-card p-4 sm:p-5 block transition-colors hover:bg-white/[0.02] group"
      style={{ borderColor: `${color}40` }}
      data-testid={`aujourdhui-item-${index}`}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <div
          className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: `${color}22`, border: `1px solid ${color}55` }}
        >
          <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[9px] uppercase tracking-widest font-semibold text-zinc-500">
              {item.module}
            </span>
            <span
              className="text-[9px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded"
              style={{ background: `${color}22`, color }}
            >
              {item.severity === "critical" ? "Critique" : "Attention"}
            </span>
          </div>
          <div className="text-sm sm:text-base font-semibold text-white mt-1" style={{ color }}>
            {item.title}
          </div>
          <div className="text-sm text-zinc-200 mt-1 leading-relaxed">
            {item.narrative}
            {item.impact_display && (
              <span className="font-semibold ml-1" style={{ color }}>· {item.impact_display}</span>
            )}
          </div>
          {item.why_now && (
            <div className="text-xs text-zinc-400 mt-2 italic flex items-start gap-1.5">
              <Zap className="w-3 h-3 mt-0.5 text-[#FFB627] shrink-0" />
              <span>{item.why_now}</span>
            </div>
          )}
          <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium transition-colors group-hover:gap-2" style={{ color }}>
            {item.cta.label}
            <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
          </div>
        </div>
      </div>
    </Link>
  );
}
